import { useQueryClient } from "@tanstack/react-query";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import axios, { AxiosRequestConfig } from "axios";
import { DateTime } from "luxon";
import { Dispatch, FC, PropsWithChildren, createContext, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { AuthSources, STS_TOKEN_REFRESH_SECONDS, StorageKeys } from "../constants/constants";
import { clearLocalSSOStorage } from "../service/sso";

export type AuthState = "signedOut" | "loading" | "signedIn" | "ssoCallback" | "emulationCallback" | "mfaChallenge" | undefined;

export interface AuthenticationContextType {
  authState: AuthState;
  previousAuthState?: AuthState;
  setAuthState: Dispatch<AuthState>;
  isUserLoggedIn: () => Promise<boolean>;
  logoutUser: () => Promise<void>;
  refreshToken: () => Promise<void>;
  getAuthToken: () => Promise<string | undefined>;
  setStsTokens: (tokens: { accessToken: string; refreshToken: string; }) => void;
}

export const AuthenticationContext = createContext<AuthenticationContextType>({
  authState: "loading",
  previousAuthState: undefined,
  isUserLoggedIn: async () => {
    return Promise.resolve(false);
  },
  logoutUser: async () => {
    return Promise.resolve();
  },
  refreshToken: async () => {
    return Promise.resolve();
  },
  getAuthToken: async () => {
    return Promise.resolve(undefined);
  },
  setStsTokens: (_: { accessToken: string; refreshToken: string; }) => {},
  setAuthState: () => {},
});

export const AuthenticationProvider: FC<PropsWithChildren> = ({ children }) => {
  const [authState, _setAuthState] = useState<AuthState>("loading");
  const [previousAuthState, setPreviousAuthState] = useState<AuthState>(undefined);
  const location = useLocation();
  const queryClient = useQueryClient();

  const setAuthState = useCallback((newAuthState: AuthState) => {
    setPreviousAuthState(authState);
    _setAuthState(newAuthState);
  }, [authState, _setAuthState, setPreviousAuthState]);

  const checkLoggedInAuthState = async (): Promise<AuthState> => {
    const loggedIn = await isUserLoggedIn();
    if (loggedIn) {
      return "signedIn";
    } else {
      return "signedOut";
    }
  };

  const setStsTokens = (tokens: { accessToken: string; refreshToken: string; }) => {
    localStorage.setItem(StorageKeys.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(StorageKeys.REFRESH_TOKEN, tokens.refreshToken);
    localStorage.setItem(StorageKeys.AUTH_SOURCE, AuthSources.CAPITALRX_STS);
  };

  useEffect(() => {
    if (location.pathname.includes("/sso/callback")) {
      setAuthState("ssoCallback");
    } else if (location.pathname.includes("/emulation")) {
      setAuthState("emulationCallback");
    } else {
      checkLoggedInAuthState()
        .then((authState) => {
          setAuthState(authState);
        })
        .catch(() => {
          setAuthState("signedOut");
        });
    }
  }, []);

  const logoutUser = useCallback(async (): Promise<void> => {
    const authSource = localStorage.getItem(StorageKeys.AUTH_SOURCE);
    if (authSource === AuthSources.AWS_COGNITO) {
      await signOut();
      setAuthState("signedOut");
    } else if (authSource === AuthSources.CAPITALRX_STS) {
      clearLocalSSOStorage();
      setAuthState("signedOut");
    }
    localStorage.removeItem(StorageKeys.AUTH_SOURCE);
    queryClient.clear();
    return Promise.resolve();
  }, [setAuthState]);

  const isUserLoggedIn = async (): Promise<boolean> => {
    const authSource = localStorage.getItem(StorageKeys.AUTH_SOURCE);
    if (authSource === AuthSources.AWS_COGNITO) {
      const session = await fetchAuthSession();
      return !!session;
    } else if (authSource === AuthSources.CAPITALRX_STS) {
      return !!localStorage.getItem(StorageKeys.ACCESS_TOKEN) && !!localStorage.getItem(StorageKeys.REFRESH_TOKEN) && !!authSource;
    } else {
      return false;
    }
  };

  const _refreshStsToken = async () => {
    const options: AxiosRequestConfig = {
      url: "/token/refresh",
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem(StorageKeys.ACCESS_TOKEN)}`,
      },
      data: {
        target_url: window.location.pathname,
        refresh_token: localStorage.getItem(StorageKeys.REFRESH_TOKEN),
      },
    };

    const client = axios.create({
      baseURL: site.baseUrl,
      headers: {
        "X-Referrer-Url": window.location.href,
      },
    });

    try {
      const response = await client(options);
      const { access_token, saml_redirect } = response.data;
      if (saml_redirect) {
        window.location.href = saml_redirect;
      } else {
        localStorage.setItem(StorageKeys.ACCESS_TOKEN, access_token);
      }
    } catch (error) {
      clearLocalSSOStorage();
      window.location.pathname = "/login";
    }
  };

  const refreshToken = async () => {
    const authSource = localStorage.getItem(StorageKeys.AUTH_SOURCE);
    if (authSource === AuthSources.AWS_COGNITO) {
      // nothing to do here, just keep on keeping on
      return Promise.resolve();
    } else if (authSource === AuthSources.CAPITALRX_STS) {
      const accessToken = localStorage.getItem(StorageKeys.ACCESS_TOKEN);
      // only run token refresh if the current access token is close to expiry
      const now = +DateTime.now();
      const currentTimeSeconds = Math.floor(now / 1000);

      let tokenData;
      try {
        const parts = accessToken.split(".");
        tokenData = JSON.parse(atob(parts[1]));
      } catch (e) {
        tokenData = { jti: "", iat: 0, exp: 0, nbf: 0, token_use: "", username: "" };
      }

      const isTokenAboutToExpire = currentTimeSeconds + STS_TOKEN_REFRESH_SECONDS >= tokenData.exp;
      if (isTokenAboutToExpire) {
        await _refreshStsToken();
        return Promise.resolve();
      }
    }

    return Promise.resolve();
  };

  const getAuthToken = async () => {
    const authSource = localStorage.getItem(StorageKeys.AUTH_SOURCE);
    if (authSource === AuthSources.AWS_COGNITO) {
      const session = await fetchAuthSession();
      if (session) {
        return session.tokens?.idToken?.toString?.();
      }
    } else if (authSource === AuthSources.CAPITALRX_STS) {
      return localStorage.getItem(StorageKeys.ACCESS_TOKEN);
    }
  };

  return (
    <AuthenticationContext.Provider
      value={{
        authState,
        previousAuthState,
        setAuthState,
        getAuthToken,
        refreshToken,
        isUserLoggedIn,
        logoutUser,
        setStsTokens,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};
